import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Coins, Loader2, Menu, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";
import { supabase } from "@/integrations/supabase/client";
import { User as SupabaseUser } from "@supabase/supabase-js";
import { useKickletPoints } from "@/hooks/useKickletPoints";
import { NotificationBell } from "@/components/NotificationBell";
import { useTiersPaused } from "@/hooks/useTiersPaused";
import { tierOf, type TierKey } from "@/config/tiers";

export function MobileBottomNav() {
  const navigate = useNavigate();
  const { toggleSidebar } = useSidebar();
  const { paused: tiersPaused } = useTiersPaused();
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [userPoints, setUserPoints] = useState<number | null>(null);
  const { getPoints, loading: pointsLoading } = useKickletPoints();
  const [userProfile, setUserProfile] = useState<{
    kick_name: string | null;
    tier: TierKey | null;
  } | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setUserProfile(null);
        setUserPoints(null);
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("kick_name, tier")
        .eq("id", user.id)
        .maybeSingle();

      if (profile) {
        setUserProfile(profile);
        if (profile.kick_name) {
          const pointsData = await getPoints(profile.kick_name);
          if (pointsData) {
            setUserPoints(pointsData.points);
          }
        }
      }
    };

    if (user) {
      fetchUserData();
    }
  }, [user]);

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-background border-t-2 border-primary/30 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_20px_rgba(0,0,0,0.5)]">
      <div className="flex h-16 items-center justify-between px-4 bg-background">
        {/* Left: Sidebar Trigger */}
        <button
          onClick={toggleSidebar}
          className="flex items-center justify-center h-12 w-12 rounded-xl bg-accent hover:bg-accent/80 transition-colors"
          aria-label="Toggle sidebar"
        >
          <Menu className="h-6 w-6 text-foreground" />
        </button>

        {/* Notification Bell (when logged in) */}
        {user && (
          <div className="flex items-center justify-center h-12 w-12 rounded-xl hover:bg-accent/60 transition-colors">
            <NotificationBell />
          </div>
        )}
        {user && userProfile ? (
          <div 
            onClick={() => navigate("/profile")}
            className="flex items-center gap-2.5 px-4 py-2 rounded-xl bg-card/60 border border-primary/20 cursor-pointer hover:bg-card/80 transition-colors"
          >
            <div className="flex items-center gap-2 text-primary">
              <Coins className="h-5 w-5" />
              {pointsLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <span className="font-bold text-base">{userPoints?.toLocaleString() ?? '—'}</span>
              )}
            </div>
            <div className="w-px h-5 bg-border/50" />
            {tiersPaused ? (
              <span className="rounded-lg bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground">Paused</span>
            ) : tierOf(userProfile.tier).image ? (
              <img
                src={tierOf(userProfile.tier).image!}
                alt={tierOf(userProfile.tier).label}
                className="h-7 w-7 object-contain"
              />
            ) : (
              <span className="text-xs font-semibold capitalize text-muted-foreground">unranked</span>
            )}
          </div>
        ) : (
          <div /> /* Empty spacer when logged out */
        )}

        {/* Right: Profile or Sign In */}
        {user ? (
          <button
            onClick={() => navigate("/profile")}
            className="flex items-center justify-center h-12 w-12 rounded-xl bg-primary/20 hover:bg-primary/30 transition-colors"
            aria-label="Go to profile"
          >
            <User className="h-6 w-6 text-primary" />
          </button>
        ) : (
          <Button
            onClick={() => navigate("/auth")}
            size="default"
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            Sign In
          </Button>
        )}
      </div>
    </nav>
  );
}

export default MobileBottomNav;
